// « Demander le paiement » (owner, 2026-10-14) — the button and its preview.
//
// One component, two places: the conversation header (WhatsApp Assistant) and
// the order page beside « Ouvrir le chat ». A person clicks; nothing sends by
// itself. The preview shows the WHOLE message:
//   • amounts and the RIB — grey, locked, read-only (the server builds them
//     from the order and the saved reply « RIB paiment »);
//   • the intro and the closing — the person's words, editable.
// Warnings (amber) never block; the one block (red) is the order's lines not
// adding up to its total, and then Envoyer is disabled.
//
// ⚠️ THE BUTTON SHIPS OFF: it shows only when the settings row says
// `payment_request_button_enabled` (migration 247) — read from the settings
// query the admin already loads, never one fetch per render.
//
// Server: worker/src/routes/paymentRequest.ts.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, X } from 'lucide-react';
import { paymentRequestApi, settingsApi, type PaymentRequestPreview } from '@/lib/api';
import { usePermissions } from '@/lib/permissionsStore';
import { useT } from '@/lib/i18n';

export type PaymentRequestRef = { order: string } | { chat: string };

const LOCALE_LABEL: Record<string, string> = {
  fr: 'Français', ar: 'Arabe', 'ary-latn': 'Darija', en: 'Anglais',
};

/** The button — only when the owner has switched it on and the person may send. */
export function PaymentRequestButton({ target, compact = false, short = false, className = '' }: {
  target: PaymentRequestRef;
  compact?: boolean;
  /** Phone header: « 💰 Paiement ». */
  short?: boolean;
  className?: string;
}) {
  const { t } = useT();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const [sentNote, setSentNote] = useState(false);
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!sentNote) return;
    const id = window.setTimeout(() => setSentNote(false), 4000);
    return () => window.clearTimeout(id);
  }, [sentNote]);
  if (settingsData?.settings?.payment_request_button_enabled !== true) return null;
  if (!can('whatsapp.manage')) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-payment-request-button title={t('Demander le paiement')}
        className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-adm-gold bg-bg-card text-adm-gold-ink font-semibold cursor-pointer hover:bg-adm-gold-wash transition-colors ${
          compact ? 'h-9 px-2.5 text-[12px]' : 'h-11 px-3 text-[12.5px]'
        } ${className}`}>
        {sentNote ? `✓ ${short ? t('Envoyée') : t('Demande envoyée')}` : `💰 ${short ? t('Paiement') : t('Demander le paiement')}`}
      </button>
      {open && (
        <PaymentRequestModal target={target} onClose={() => setOpen(false)}
          onSent={() => { setOpen(false); setSentNote(true); }} />
      )}
    </>
  );
}

/** The preview + Envoyer. */
export function PaymentRequestModal({ target, onClose, onSent, initial }: {
  target: PaymentRequestRef;
  onClose: () => void;
  onSent: () => void;
  /** Tests / design harness: a preview to show without asking the server. */
  initial?: PaymentRequestPreview;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const key = 'order' in target ? `order:${target.order}` : `chat:${target.chat}`;
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['payment-request-preview', key],
    queryFn: () => paymentRequestApi.preview(target),
    initialData: initial,
    enabled: !initial,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const draft = data?.result.ok ? data.result.draft : null;
  const [edits, setEdits] = useState<Record<string, string>>({});
  // A new preview (opened again, amounts moved) restarts from its own words.
  useEffect(() => {
    if (!draft) return;
    const next: Record<string, string> = {};
    for (const p of draft.parts) if (!p.locked) next[p.key] = p.text;
    setEdits(next);
  }, [draft?.lockHash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = useMutation({
    mutationFn: () => {
      const order = data?.order.shopify_order_id ?? ('order' in target ? target.order : '');
      return paymentRequestApi.send({
        order, lock_hash: draft?.lockHash ?? '',
        edits: { intro: edits.intro, closing: edits.closing },
      });
    },
    onSuccess: () => {
      for (const k of ['whatsapp-conversation', 'whatsapp-conversations', 'whatsapp-conversation-order', 'order', 'payment-request-preview']) {
        void qc.invalidateQueries({ queryKey: [k] });
      }
      onSent();
    },
  });

  const blocked = data && !data.result.ok ? data.result.blocked : null;
  const sendErr = send.error as (Error & { status?: number }) | null;
  const stale = sendErr?.message?.includes('rouvrez');
  const emptyEdit = draft ? draft.parts.some((p) => !p.locked && p.key === 'intro' && !(edits[p.key] ?? '').trim()) : false;
  const canSend = !!draft && data?.enabled === true && !send.isPending && !isFetching && !emptyEdit;

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog" aria-modal="true" aria-label={t('Demander le paiement')} onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(46,36,24,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />
      <div onClick={(e) => e.stopPropagation()} data-payment-request-modal
        className="relative w-full sm:max-w-xl max-h-[92vh] sm:max-h-[88vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl bg-bg-card border border-border-subtle">

        {/* Header */}
        <div className="shrink-0 flex items-start gap-3 px-5 py-4 border-b border-border-subtle bg-bg-elevated">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold leading-tight text-text-primary" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              💰 {t('Demander le paiement')}
            </h3>
            <p className="text-xs mt-0.5 text-text-secondary truncate">
              {t('Commande')} <span className="font-semibold text-adm-gold-ink">{data?.order.name ?? '…'}</span>
              {data && (<> · {t('Langue de la cliente')} : <span className="font-medium">{data.client_locale ? t(LOCALE_LABEL[data.client_locale] ?? data.client_locale) : t('inconnue')}</span></>)}
            </p>
            {draft?.localeFallback && (
              <p className="text-[11.5px] mt-1 text-text-tertiary">{t('Version darija pas encore écrite — message en français')}</p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="shrink-0 w-8 h-8 rounded-lg border border-border-subtle text-text-tertiary hover:text-text-primary flex items-center justify-center cursor-pointer">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
          {isLoading && <p className="text-[13px] text-text-tertiary">{t('Préparation du message…')}</p>}
          {error && <p className="text-[13px] text-status-stuck">{(error as Error).message}</p>}

          {data && data.enabled !== true && (
            <p className="text-[12.5px] rounded-lg px-3 py-2 bg-bg-elevated text-text-secondary border border-border-subtle">
              {t('Le bouton est désactivé dans les réglages — aperçu seulement.')}
            </p>
          )}

          {data && data.warnings.length > 0 && (
            <ul className="rounded-lg px-3 py-2 space-y-1 bg-status-waiting-bg border border-status-waiting-border" data-payment-warnings>
              {data.warnings.map((w) => (
                <li key={w} className="text-[12.5px] leading-snug text-status-waiting">⚠️ {w}</li>
              ))}
            </ul>
          )}

          {blocked && (
            <div className="rounded-lg px-3 py-2.5 bg-status-stuck-bg border border-status-stuck-border" data-payment-blocked>
              <p className="text-[12.5px] font-semibold text-status-stuck">⛔ {t('Envoi impossible')}</p>
              <p className="text-[12.5px] leading-snug text-status-stuck mt-0.5">{blocked}</p>
              {data && !data.result.ok && data.result.lines.length > 0 && (
                <ul className="mt-2 text-[12px] text-text-secondary tabular-nums">
                  {data.result.lines.map((l, i) => <li key={i}>• {l.label} : {l.amount} MAD</li>)}
                  {data.result.total != null && <li className="font-semibold mt-0.5">{t('Total de la commande')} : {data.result.total} MAD</li>}
                </ul>
              )}
            </div>
          )}

          {draft && draft.parts.map((p) => p.locked ? (
            <div key={p.key} className="relative rounded-xl px-3.5 py-3 bg-adm-surface-2 border border-border-subtle" data-part={p.key} data-locked>
              <span className="absolute top-2 right-2.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                <Lock className="h-3 w-3" aria-hidden /> {t('Verrouillé')}
              </span>
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-text-secondary pr-20 tabular-nums select-text">{p.text}</p>
            </div>
          ) : (
            <div key={p.key} className="relative" data-part={p.key}>
              <span className="absolute -top-2 right-3 z-10 px-2 py-px rounded-full text-[9.5px] font-bold tracking-wide bg-bg-card text-[#0B7E5B] dark:text-[#25D366]"
                style={{ border: '1px solid rgba(37,211,102,.45)' }}>
                {t('✏️ MODIFIABLE')}
              </span>
              <textarea
                value={edits[p.key] ?? p.text}
                onChange={(e) => setEdits((s) => ({ ...s, [p.key]: e.target.value }))}
                rows={Math.min(6, Math.max(2, (edits[p.key] ?? p.text).split('\n').length + 1))}
                aria-label={p.key === 'intro' ? t('Début du message') : t('Fin du message')}
                className="w-full rounded-xl px-3.5 py-3 text-[13px] leading-relaxed text-text-primary outline-none resize-y focus:ring-2 focus:ring-[#25D366]/40 bg-[#F2FBF5] dark:bg-adm-surface-2"
                style={{ border: '1px solid rgba(37,211,102,.35)' }}
              />
            </div>
          ))}

          {draft && (
            <p className="text-[11.5px] text-text-tertiary">
              {t('Les montants et le RIB viennent de la commande et ne se modifient pas ici. L’assistant reste actif pour répondre à ses questions.')}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-3 border-t border-border-subtle bg-bg-elevated">
          {sendErr && (
            <p className="text-[12.5px] text-status-stuck mb-2">
              {sendErr.message}
              {stale && (
                <> · <button type="button" className="underline cursor-pointer" onClick={() => { send.reset(); void refetch(); }}>{t('Rouvrir l’aperçu')}</button></>
              )}
            </p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-border-default bg-bg-card text-text-secondary hover:bg-bg-elevated cursor-pointer">
              {t('Annuler')}
            </button>
            <button type="button" onClick={() => send.mutate()} disabled={!canSend}
              className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-[#25D366] hover:bg-[#1ebe5a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
              {send.isPending ? t('Envoi…') : t('Envoyer')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
